const LOGIN_PATH = '/login';
const HOME_PATH = '/welcome';

// 401s from these endpoints mean "wrong credentials / code", not "your session ended".
const PUBLIC_AUTH_URLS = [
  'auth/user_login',
  'auth/user_signup',
  'auth/google',
  'auth/forget_password',
  'auth/send_otp',
  'auth/verify_otp',
  'auth/reset_password',
];

type Navigate = (path: string) => void;

// The only parts of a rejected axios request this module reads.
export interface RejectedRequest {
  response?: { status?: number };
  config?: { url?: string; headers?: { authorization?: unknown } };
}

const isRejectedRequest = (error: unknown): error is RejectedRequest =>
  typeof error === 'object' && error !== null;

const defaultNavigate: Navigate = (path) => window.location.assign(path);

// redux-persist's copies of the editor/project/user slices. They live in localStorage, shared by all
// tabs, so left behind they rehydrate the previous account's project into the next session.
const PERSISTED_KEYS = ['persist:root', 'persist:project', 'persist:editor', 'persist:user'];

export const clearSession = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  PERSISTED_KEYS.forEach((key) => localStorage.removeItem(key));
};

// Every sign-in / sign-up goes through here so a new session never inherits the previous
// account's persisted workspace (this is also what a second tab signing in as another account hits).
export const startSession = (token: string, user: unknown): void => {
  PERSISTED_KEYS.forEach((key) => localStorage.removeItem(key));
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
};

// The account id carried in a "Bearer <jwt>" token, or null if it can't be read.
const userIdFromToken = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const payload = token.replace('Bearer ', '').split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json)?.data?.id ?? null;
  } catch {
    return null;
  }
};

// axios response-error handler: a 401 on an authenticated request means the session is gone
// (expired, revoked by logout, or the user no longer exists) -> clear it and go to /login.
export const createSessionErrorHandler = (navigate: Navigate = defaultNavigate) => {
  return (error: unknown): Promise<never> => {
    const failed: RejectedRequest = isRejectedRequest(error) ? error : {};
    const status = failed.response?.status;
    const authorization = failed.config?.headers?.authorization;
    const sentToken = typeof authorization === 'string' ? authorization : undefined;
    const url = failed.config?.url ?? '';
    const isPublicAuthCall = PUBLIC_AUTH_URLS.some((publicUrl) => url.includes(publicUrl));

    // Only react if the rejected request carried the token that is still current: a late 401
    // for an old token must not log out an account another tab has since signed in to.
    if (status === 401 && sentToken && !isPublicAuthCall && localStorage.getItem('token') === sentToken) {
      clearSession();
      navigate(LOGIN_PATH);
    }
    return Promise.reject(error);
  };
};

// `storage` fires only in the *other* tabs of the origin. If the account changed or the session
// ended there, this tab must stop acting as the identity it still displays.
export const createStorageHandler = (navigate: Navigate = defaultNavigate) => {
  return (event: StorageEvent): void => {
    if (event.key === null) {
      navigate(LOGIN_PATH);
      return;
    }
    if (event.key !== 'token') return;
    if (!event.newValue) {
      navigate(LOGIN_PATH);
      return;
    }
    if (userIdFromToken(event.oldValue) !== userIdFromToken(event.newValue)) {
      navigate(HOME_PATH);
    }
  };
};
